export {};

// External globals from other scripts loaded on same page
declare function buildColorFilterFragment(): string;
declare function buildDesignFilterFragment(): string;
declare function buildfcuQuery(collectionHandle: string, first: number | string, cursor: string | null): string;
declare function buildaQuery(collectionHandle: string, first: number | string, cursor: string | null): string;
// Swiper loaded globally via CDN
declare const Swiper: new (selector: string, options: Record<string, unknown>) => unknown;

// Types for Storefront GraphQL product responses
interface FCImageNode {
  url: string;
  altText: string | null;
  id?: string;
}

interface FCImagesConnection {
  edges: { node: FCImageNode }[];
}

interface FCVariantBySelectedOptions {
  id: string;
  image: { url: string; altText: string | null } | null;
}

interface FCPriceRange {
  maxVariantPrice: { amount: string; currencyCode: string };
  minVariantPrice: { amount: string; currencyCode: string };
}

interface FCProduct {
  id: string;
  handle: string;
  title: string;
  productType: string;
  createdAt: string;
  availableForSale: boolean;
  tags: string[];
  featuredImage: FCImageNode | null;
  priceRange: FCPriceRange;
  variantBySelectedOptions?: FCVariantBySelectedOptions | null;
  variantYes?: FCVariantBySelectedOptions | null;
  variantNo?: FCVariantBySelectedOptions | null;
  images?: FCImagesConnection;
}

interface FCProductsConnection {
  pageInfo: { hasNextPage: boolean; endCursor: string };
  edges: { node: FCProduct }[];
}

interface FCGraphQLResponse {
  data?: {
    collection?: {
      products?: FCProductsConnection;
    };
  };
}

// Configuration
const fcState = {
  currentLanguage: document.documentElement.lang.toUpperCase(),
};


// Utilitaires
function setCookie(name: string, value: string, days = 14): void {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge};`;
}

function getCookie(name: string): string | undefined {
    return document.cookie
    .split('; ')
    .find(row => row.startsWith(name + '='))
    ?.split('=')[1];
}


// Fonctions de construction des requêtes GraphQL
function buildQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null, filters: string | null = null, queryAddon = ''): string {
  const country = (document.querySelector('body[data-country]') as HTMLElement | null)?.dataset.country?.toUpperCase() === 'CA' ? 'CA' : 'US';
  const lang = document.documentElement.lang.toUpperCase();
return `
  query FilteredProducts @inContext(country: ${country},language: ${lang}) {
    collection(handle: "${collectionHandle}") {
      products(first: ${first}, sortKey: BEST_SELLING ${cursor ? `, after: "${cursor}"` : ''}${filters ? `, filters: ${filters}` : ''}) {
          pageInfo {
              hasNextPage
              endCursor
          }
          edges {
              node {
                id
                handle
                title
                productType
                createdAt
                tags
                ${queryAddon}
                featuredImage {
                    url
                    altText
                    id
                }
                priceRange {
                    maxVariantPrice {
                    amount
                    currencyCode
                    }
                    minVariantPrice {
                    amount
                    currencyCode
                    }
                }
              }
          }
        }
      }
    }
  `;
}

function buildtcQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = `[{ productType: "Phone Case"}]`;
  const phoneDeviceValue = typeof getCookie('favPhone') === 'string' ? getCookie('favPhone'): 'iPhone 17';
  const queryAddon = `variantYes: variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"},{ name:"Case Type", value:"Impact (Gloss)"},{ name:"MagSafe Ring", value:"Yes"}], ignoreUnknownOptions: true) {
      id
      image {
        url
        altText
      }
  }
  variantNo: variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"},{ name:"Case Type", value:"Impact (Gloss)"},{ name:"MagSafe Ring", value:"No"}], ignoreUnknownOptions: true) {
      id
      image {
        url
        altText
      }
  }`;
  return buildQuery(collectionHandle, first, cursor, filters, queryAddon);
}

function buildccQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = '[{ productType: "Clear Case"}]';
  const phoneDeviceValue = typeof getCookie('favPhone') === 'string' ? getCookie('favPhone'): 'iPhone 17';

  const queryAddon = `variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"},{ name:"Case Type", value:"Impact (Gloss)"}]) {
      id
      image {
          url
          altText
      }
    }`;
  return buildQuery(collectionHandle, first, cursor, filters, queryAddon);
}

function buildacQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = `[{ productType: "Airpod Cases"}]`;
  const queryAddon = `images(first: 2) {
      edges {
          node {
            url
            altText
          }
      }
    }`;
  return buildQuery(collectionHandle, first, cursor, filters, queryAddon);
}

function buildmsQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = '[{ productType: "Skin"}]';
  return buildQuery(collectionHandle, first, cursor, filters);
}

function buildicQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = '[{ productType: "iPad Folio Case"} ]';
  const queryAddon = `images (first: 3) {
    edges {
      node {
          id
          url
          altText
      }
    }
  }`;
  return buildQuery(collectionHandle, first, cursor, filters, queryAddon);
}

function buildiccQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = '[{ productType: "iPad Clear Case"} ]';
  return buildQuery(collectionHandle, first, cursor, filters);
}

function buildfcQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = '[{ productType: "Folio Case"},{productMetafield: {namespace: "custom", key: "folio_plain", value: "0"}}]';
  let phoneDeviceValue = typeof getCookie('favPhone') === 'string' ? getCookie('favPhone')! : 'iPhone 17';
  if(!phoneDeviceValue.includes('Galaxy S2') && !phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const queryAddon = `variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}]) {
      id
      image {
        url
        altText
      }
  }`;
  return buildQuery(collectionHandle, first, cursor, filters, queryAddon);
}

// NOTE: buildkcQuery signature differs from other build*Query functions (same issue as pre-filtered).
// When called from switch as buildkcQuery(collectionHandle, nb_products, cursor),
// values flow correctly despite misleading param names.
function buildkcQuery(first: string | number = 12, cursor: string | number | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const filters = `[{ productType: "Kindle Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}]`;
  const queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }`;
  return buildQuery(first as string, cursor as string, filters, queryAddon);
}

const CARD_IMAGE_WIDTHS = [200, 250, 300, 350, 500, 600, 700];
const CARD_IMAGE_SIZES = '(min-width: 1200px) 300px, (min-width: 990px) 250px, (min-width: 750px) 350px, (min-width: 500px) 250px, 200px';

function buildResponsiveCardImg(url: string, alt: string | null | undefined): string {
  const srcset = CARD_IMAGE_WIDTHS.map((w) => `${url}&width=${w} ${w}w`).join(', ');
  return `<img src="${url}&width=300" srcset="${srcset}" sizes="${CARD_IMAGE_SIZES}" alt="${alt}" loading="lazy">`;
}

function  buildImage(currentProduct: FCProduct): string {
    let imageurl = currentProduct.featuredImage ? currentProduct.featuredImage.url : '';
    let imagealt = currentProduct.featuredImage ? currentProduct.featuredImage.altText : '';
    const selectedVariant = currentProduct.variantYes || currentProduct.variantNo || currentProduct.variantBySelectedOptions;
    if(selectedVariant) {
        imageurl = selectedVariant.image ? selectedVariant.image.url : '';
        imagealt = selectedVariant.image ? selectedVariant.image.altText : '';
    }
    let hoveraddon = '';
    if (currentProduct.productType == 'Folio Case' && currentProduct.featuredImage) {
      const plainTag = currentProduct.tags.filter(tag => {
        return tag == 'design:Plain';
      });
      if (plainTag.length == 0 && currentProduct.featuredImage) {
        const hoverURL = currentProduct.featuredImage.url;
        hoveraddon = `<div class="media media--transparent media--hover">
            ${buildResponsiveCardImg(hoverURL, hoverURL)}
          </div>`;
      }
    } else if (currentProduct.productType == 'iPad Folio Case') {
      const image = currentProduct.images?.edges[2]?.node;
      if (image) {
        const hoverURL = image.url;
        const hoverAlt = image.altText;
        hoveraddon = `<div class="media media--transparent media--hover">
            ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
          </div>`;
      }
    } else if (currentProduct.productType == 'Airpod Cases') {
      const image = currentProduct.images?.edges[1]?.node;
      if (image) {
        const hoverURL = image.url;
        const hoverAlt = image.altText;
        hoveraddon = `<div class="media media--transparent media--hover">
            ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
          </div>`;
      }
    } else if (currentProduct.productType == 'ChargePods') {
      const image = currentProduct.images?.edges[1]?.node;
      if (image) {
        const hoverURL = image.url;
        const hoverAlt = image.altText;
        hoveraddon = `<div class="media media--transparent media--hover">
            ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
          </div>`;
      }
    }

    const imageSection = `<div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${buildResponsiveCardImg(imageurl, imagealt)}
                </div>
                ${hoveraddon}
            </div>`;
    return imageSection;
}

function buildcpQuery(collectionHandle: string, first: number | string = 12, cursor: string | null = null): string {
  const filters = '[{ productType: "ChargePods"}]';
  return buildQuery(collectionHandle, first, cursor, filters);
}

// Fonction pour construire le HTML d'un produit
function buildProductHtml(product: FCProduct): string {
    const { id, title, handle, availableForSale, variantBySelectedOptions, variantYes, variantNo, tags, productType, priceRange } = product;

    const selectedVariant = variantYes || variantNo || variantBySelectedOptions;
    let variantId = selectedVariant ? selectedVariant.id.replace("gid://shopify/ProductVariant/", "") : null;
    let productUrl = handle;
    if(variantId) {
        productUrl = productUrl + '?variant=' + variantId;
    }

    let new_title = title;

    if (new_title.indexOf("clear case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("clear case"));
    }
    if (new_title.indexOf("Clear Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("Clear Case"));
    }
    if (new_title.indexOf("Clear case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("Clear case"));
    }
    if (new_title.indexOf("AirPods Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("AirPods Case"));
    }
    if (new_title.indexOf("AirPod Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("AirPod Case"));
    }
    if (new_title.indexOf("AirPod case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("AirPod case"));
    }
    if (new_title.indexOf("AirPods case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("AirPods case"));
    }
    if (new_title.indexOf(" - iPad Folio") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" - iPad Folio"));
    }
    if (new_title.indexOf(" iPad Folio") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" iPad Folio"));
    }
    if (new_title.indexOf(" - iPad") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" - iPad"));
    }
    if (new_title.indexOf(" iPad") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" iPad"));
    }
    if (new_title.indexOf("MacBook skin") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("MacBook skin"));
    }
    if (new_title.indexOf(" - Folio Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" - Folio Case"));
    }
    if (new_title.indexOf(" ChargePods") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" ChargePods"));
    }
    if (new_title.indexOf(" ChargePod") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" ChargePod"));
    }

    let preprice = document.documentElement.lang.toUpperCase() == 'FR' ? 'À partir de' : 'From';
    if(priceRange.maxVariantPrice.amount == priceRange.minVariantPrice.amount) {
      preprice = '';
    } else if (productType.indexOf('iPad') > -1) {
      preprice = '';
    }
    const urlPrefix = document.documentElement.lang.toUpperCase() == 'FR' ? '/fr' : '';

    let forceBadge = '';

    if(document.body.dataset.forceBadge == 'true' && document.body.dataset.forceBadgeType?.includes(productType)) {
      forceBadge = `<span class="badge badge--${document.body.dataset.forceBadgeColor}">${document.body.dataset.forceBadgeText}</span>`;
    }

    let oosBadge = '';
    let lowStockBadge = '';
    let newBadge = '';
    let finalSaleBadge = '';

    const newTag = tags.filter(tag => {
      return tag == 'New';
    });
    const finalSaleTag = tags.filter(tag => {
      return tag == 'final_sale';
    });

    if(!availableForSale) {
      oosBadge = fcState.currentLanguage == 'FR' ? '<span class="badge">Épuisé</span>':'<span class="badge">Out of Stock</span>';
    } else {
      const lowTag = tags.filter(tag => {
        return tag == 'low-stock';
      });
      if(lowTag.length > 0) {
        lowStockBadge = fcState.currentLanguage == 'FR' ? '<span class="badge">Bientôt épuisé</span>':'<span class="badge">Low Stock</span>';
      }
    }

    if(newTag.length > 0) {
      newBadge = fcState.currentLanguage == 'FR' ? '<span class="badge">Nouveau</span>':'<span class="badge">New</span>';
    }

    if(finalSaleTag.length > 0) {
      finalSaleBadge = fcState.currentLanguage == 'FR' ? '<span class="badge">Vente finale</span>':'<span class="badge">Final Sale</span>';
    }

    const quickAddLabel = document.documentElement.lang.toUpperCase() == 'FR' ? 'Ajout rapide':'Quick Add';

    const productID = id.replace("gid://shopify/Product/", "");
    const quickAdd = `<div class="quick-add no-js-hidden">
        <modal-opener data-modal="#QuickAdd-${productID}">
          <button id="quick-add-template--${productID}-submit"
            type="submit" name="add"
            class="quick-add__submit button button--full-width button--outline" aria-haspopup="dialog"
            aria-labelledby="quick-add-template--${productID} title-template--${productID}"
            data-product-url="${urlPrefix}/products/${productUrl}">
            ${quickAddLabel}
            <div class="loading__spinner hidden">
              <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
            </div>
          </button>
        </modal-opener>
        <quick-add-modal id="QuickAdd-${productID}" class="quick-add-modal">
          <div
            role="dialog"
            aria-label="${new_title}"
            aria-modal="true"
            class="quick-add-modal__content global-settings-popup"
            tabindex="-1"
          >
            <button
              id="ModalClose-${productID}"
              type="button"
              class="quick-add-modal__toggle button button--outline"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
              </svg>
            </button>
            <div id="QuickAddInfo-${productID}" class="quick-add-modal__content-info"></div>
          </div>
        </quick-add-modal>
      </div>`;
    const clearCondition = productType.indexOf('Clear Case') > -1 && productType.indexOf('iPad') == -1;

    // FIX: original had `price = 39.95` BEFORE `let price = ...` declaration (TDZ error)
    let price: number | string = clearCondition ? 34.99 : priceRange.minVariantPrice.amount;
    if(title.indexOf("DIY Queen Clear Case") > -1 || title.indexOf("DIY Queen transparent") > -1) {
      price = 39.95;
    }
    if (productType.indexOf('ChargePods') > -1) {
      price = 79.99;
      preprice = "";
    }

    const enPriceTag = document.documentElement.lang.toUpperCase() == 'EN' ? '$' : '';
    const frPriceTag = document.documentElement.lang.toUpperCase() == 'FR' ? '$' : '';

    return `
      <li class="grid__item swiper-slide">
        <div class="card-wrapper product-card-wrapper underline-links-hover ${productType.indexOf('Airpod') > -1 ? '--airpods' : ''} ${productType.indexOf('iPad') > -1  ? '--ipad' : ''}">
          <a href="${urlPrefix}/products/${productUrl}">
            <span class="visually-hidden">${new_title}</span>
          </a>
          <div class="card card--standard card--media">
            <div class="card__inner">
              ${buildImage(product)}
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${productUrl}" class="full-unstyled-link">
                    ${new_title}
                  </a>
                </h3>
                <div class="card-information">
                  <div class="price">
                    <div class="price__container">
                      <div class="price__regular">
                        ${preprice} ${enPriceTag}${price}${frPriceTag}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!--${quickAdd}-->
            </div>
            <div class="card__badge top left">
              ${newBadge}
              ${lowStockBadge}
              ${oosBadge}
              ${forceBadge}
              ${finalSaleBadge}
            </div>
          </div>
        </div>
      </li>
    `;
  }

  // Fonction de fetch principale
async function fetchCollectionProductsByProductType(Manager: ProductTypeManager, isReset = true, cursor: string | null = null): Promise<void | false> {
    const currentBuild = Manager.sectionType;
    try {
        let query: string | undefined;
        const currentUL = Manager.section;
        const nb_products = currentUL.dataset.ogn || '12';
        const collectionHandle = currentUL.dataset.collectionHandle || '';
        switch (currentBuild) {
            case 'tc':
                query = buildtcQuery(collectionHandle, nb_products, cursor);
                break;
            case 'fc':
                query = buildfcQuery(collectionHandle, nb_products, cursor);
                break;
            case 'fcu':
                query = buildfcuQuery(collectionHandle, nb_products, cursor);
                break;
            case 'cc':
                query = buildccQuery(collectionHandle, nb_products, cursor);
                break;
            case 'ac':
                query = buildacQuery(collectionHandle, nb_products, cursor);
                break;
            case 'ic':
                query = buildicQuery(collectionHandle, nb_products, cursor);
                break;
            case 'icc':
                query = buildiccQuery(collectionHandle, nb_products, cursor);
                break;
            case 'ms':
                query = buildmsQuery(collectionHandle, nb_products, cursor);
                break;
            case 'a':
                query = buildaQuery(collectionHandle, nb_products, cursor);
                  break;
            case 'cp':
                query = buildcpQuery(collectionHandle, nb_products, cursor);
                break;
            case 'kc':
                query = buildkcQuery(collectionHandle, nb_products);
                break;
        }

        const section = Manager.section;
        if (!section) {
        console.warn(`Section ${currentBuild} not found`);
        return;
    }
    const response = await fetch('https://kaseme-dev-page.myshopify.com/api/2026-01/graphql.json', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': "a815422836370e8d05bd0c6a813dbaa5"
        },
        body: JSON.stringify({
            query: query
        })
    });

    if (!response.ok) throw new Error('Network response was not ok');
    const data: FCGraphQLResponse = await response.json();
    const products = data.data?.collection?.products;
    if(products && products.edges.length > 0) {
        if (isReset) {
            section.innerHTML = '';
        }
        let productHTML = '';
        products.edges.forEach(({ node: product }) => productHTML += buildProductHtml(product));

        section.innerHTML += productHTML;

    } else {
        section.remove();
        Manager.isValid = false;
        }
        return;
    } catch (error) {
        console.error('Error fetching products:', error);
        return false;
    }
}

// Gestionnaire de produits
class ProductTypeManager {
  section: HTMLElement;
  isValid!: boolean;
  sectionType!: string;

  constructor(section: HTMLElement, sectionType: string) {
      this.section = section;
      if (!this.section) return;
      this.isValid = true;
      this.sectionType = sectionType;

      fetchCollectionProductsByProductType(
      this
      ).then(() => {
      const parentEl = this.section.parentElement;
      const grandparentEl = parentEl?.parentElement;
      if (parentEl && grandparentEl) {
        new Swiper(`#${parentEl.id}`, {
            loop: false,
            slidesPerView: 2.5,
            spaceBetween: 10,
            centeredSlides: false,
            centerInsufficientSlides: true,
            lazy: true,
            scrollbar: {
                el: `#${grandparentEl.id} .swiper-scrollbar`,
                hide: true,
            },
            breakpoints: {
            1200: {
                slidesPerView: 5.5
            },
            750: {
                slidesPerView: 4.5
            }
            },
            navigation: {
                nextEl: `#${grandparentEl.id} [data-next]`,
                prevEl: `#${grandparentEl.id} [data-prev]`,
            },
        });
      }

      this.section.classList.remove('--not-loaded');
      });
  }
}

    // Initialisation
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-section]').forEach(section => {
    const el = section as HTMLElement;
    if (el.dataset.section) {
      new ProductTypeManager(el, el.dataset.section);
    }
  });
});
